import "./index.css";
import { AuthZTable, defaultPageSize, defaultPageSizeOptions } from "../../../../../../components/authZTable";
import { ReviewHistoryController } from "./controller";
import React from "react";
import { StatusComponent } from "../../../../../../components/statusComponent";
import { DelegationComponent } from "../../../../../../components/delegationComponent";
import { renderWithStyle } from "../../../../../../common/utils";
import { isActiveReview } from "./model";
import { ReviewTaskAction, sendReviewTaskEvent } from "../../../../../../components/matomoClient";
import { TextWithSubTitle } from "../../../../../../components/textWithSubTitle";
import DecisionSummary from "../../../../../../components/DecisionSummary";
import {DownloadFiles} from "../../../../download-files";
import {fetchAvailableDownloadsForApplicationReview} from "../../../../download-files/service";

export const ApplicationsReview = (
  {
    appInfo,
    appId,
    reload,
    pageSize,
    setTabPageSize,
    setActiveTab,
  }) => {
  const { onReviewClick, columns, dataSource, isLoading } = ReviewHistoryController(
    appId,
    reload,
    setActiveTab,
    appInfo,
  );

  const rowClassName = (record) => {
    if (isActiveReview(record.status.status)) {
      return "table-row-highlighted";
    } else {
      return "table-row-disabled";
    }
  };

  const modifiedColumns = columns?.map((column) => {
    let searchEnabled = true;
    let sortEnabled = true;
    let { render, ...restKeys } = column;
    ({ sortEnabled, render, restKeys } = getColumnPropsBasedOnColumnTitle(
      column,
      sortEnabled,
      render,
      rowClassName,
      onReviewClick,
      appId,
      setActiveTab,
      restKeys,
    ));
    ({ render, searchEnabled, sortEnabled, restKeys } = getColumnPropsBasedOnRender(
      render,
      searchEnabled,
      sortEnabled,
      restKeys,
      appInfo,
      column,
      appId
    ));

    return { ...restKeys, render, searchEnabled, sortEnabled };
  });

  return (
    <AuthZTable
      name={"reviewHistory"}
      id={`reviewHistory-${appId}`}
      columns={modifiedColumns}
      dataSource={dataSource}
      rowClassName={rowClassName}
      pagination={{
        showSizeChanger: true,
        pageSizeOptions: defaultPageSizeOptions,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        defaultPageSize: defaultPageSize,
        pageSize: pageSize,
        defaultCurrent: 1,
        showQuickJumper: true,
        onShowSizeChange: (current, size) => {
          setTabPageSize({ pageSize: size });
        },
      }}
      className="time-table-row-select"
      scroll={{ y: "calc(100vh - 365px)" }}
      loading={isLoading}
    />
  );
};

function getColumnPropsBasedOnRender(
  render,
  searchEnabled,
  sortEnabled,
  restKeys,
  appInfo,
  column,
  appId
) {
  function actionColumnProps() {
    searchEnabled = false;
    sortEnabled = false;
    restKeys.width = "12%";
    render = ({ status }, { reviewId, name }) => {
      return (
          <>
              <DownloadFiles
                  appProps={{name: appInfo.name, id: appId}}
                  fetchAvailableDownloadableFiles={fetchAvailableDownloadsForApplicationReview}
                  reviewProps={{id: reviewId, name: name, status: status}}
                  popoverPlacement="bottom"
                  showOnlyLatestFiles={false}
              />
          </>
      );
    };
  }

  function endColumnProps() {
    restKeys.width = "8%";
    sortEnabled = false;
    render = ({ endDate, closedBy }) => <TextWithSubTitle titles={[endDate, closedBy]}/>;
  }

  function startColumnProps() {
    sortEnabled = false;
    restKeys.width = "8%";
    render = ({ startDate, startedBy }) => <TextWithSubTitle titles={[startDate, startedBy]}/>;
  }

  function decisionColumnProps() {
    searchEnabled = false;
    sortEnabled = false;
    restKeys.width = "15%";
    render = (decisionSummary) => (
      <DecisionSummary
        noChangeNeeded={decisionSummary["No change needed"]}
        needsAdjustment={decisionSummary["Needs adjustment"]}
        needsRemoval={decisionSummary["Needs removal"]}
        notReviewed={decisionSummary["Not reviewed"]}
      />
    );
  }

  function delegationColumnProps(hasSendReminder) {
    searchEnabled = false;
    sortEnabled = false;
    render = (delegatedByList, review) => (
      <DelegationComponent
        status={review.status.status}
        appInfo={appInfo}
        delegatedByList={delegatedByList}
        hasSendReminder={hasSendReminder}
      />
    );
  }

  function statusColumnProps() {
    searchEnabled = false;
    sortEnabled = false;
    restKeys.width = "19%";
    render = ({ status, completedTasks, totalTasks }) => {
      return (
        <StatusComponent
          status={status}
          completedTasks={completedTasks}
          totalTasks={totalTasks}
        />
      );
    };
  }

  switch (render) {
    case "StatusComponent": {
      statusColumnProps();
      break;
    }
    case "DelegationComponentWithReminder": {
      delegationColumnProps(true);
      break;
    }
    case "DelegationComponent": {
      delegationColumnProps(false);
      break;
    }
    case "DecisionComponent": {
      decisionColumnProps();
      break;
    }
    case "TextWithSubTitleComponent": {
      if (column.title === "Start Date & Started By") {
        startColumnProps();
      }
      if (column.title === "End Date & Closed By") {
        endColumnProps();
      }
      break;
    }
    case "ActionsComponent": {
      actionColumnProps();
      break;
    }
    default: {
      return { render, searchEnabled, sortEnabled, restKeys };
    }
  }
  return { render, searchEnabled, sortEnabled, restKeys };
}

function getColumnPropsBasedOnColumnTitle(
  column,
  sortEnabled,
  render,
  rowClassName,
  onReviewClick,
  appId,
  setActiveTab,
  restKeys,
) {
  function reviewNameColumnProps() {
    sortEnabled = false;
    render = (name, { reviewId, ...record }) => {
      const status = record.status?.status;
      const className = rowClassName(record);
      return renderWithStyle(
        { className },
        <a
          href="#"
          onClick={(_e) => {
            onReviewClick({ reviewId, name, status, appId });
            setActiveTab(reviewId);
            sendReviewTaskEvent(ReviewTaskAction.ViewReviewTasks, name);
          }}
        >
          <span className={"linkSpan"}>{name}</span>
        </a>,
      );
    };
  }

  switch (column.title) {
    case "Review Name": {
      reviewNameColumnProps();
      break;
    }
    case "Comment": {
      restKeys.width = "12%";
      break;
    }
    default: {
      return { sortEnabled, render, restKeys };
    }
  }
  return { sortEnabled, render, restKeys };
}
